import React, { useEffect, useState, memo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { generateFoodImage } from '../../services/leonardoService';
import { useRecipeCard } from '../../hooks/useRecipeCard';
import './AnalizedCard.css';
 const getLevelColor = (level) => {
  switch (level.toLowerCase()) {
    case 'easy':
      return '#4CAF50';
    case 'medium':
      return '#FFA500';
    case 'hard':
      return '#F44336';
    default:
      return '#FFFFFF';
  }
};
const Card = styled(motion.div)`
  display: flex;
  background: rgba(17, 17, 17, 0.95);
  border-radius: 12px;
  width: 100%;
  max-height: 140px;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
  font-family: 'Roboto', sans-serif;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100px;
  max-height: 130px;
  overflow: hidden;
  border-radius: 12px;
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Image = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ContentContainer = styled.div`
  flex: 1;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.h3`
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  padding: 0;
  white-space: nowrap;
  color: #fff;
  text-overflow: ellipsis;
  width: 100%;
  line-height: 1.2;
  font-family: 'Roboto', sans-serif;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 3px 0;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Roboto', sans-serif;
`;

const InfoText = styled.span`
  color: ${props => props.color || 'white'};
  font-size: 12px;
  font-weight: 400;
`;

const LevelBadge = styled.div`
  color: ${props => {
    switch (props.level?.toLowerCase()) {
      case 'easy': return '#4AD3B4';
      case 'medium': return '#FFC99C';
      case 'hard': return '#FF7676';
      default: return '#4AD3B4';
    }
  }};
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IngredientsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 2px;
`;

const IngredientsLabel = styled.div`
  color: #4CAF50;
  font-size: 11px;
  font-weight: bold;
  font-family: 'Roboto', sans-serif;
`;

const IngredientsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Ingredient = styled.div`
  background: #4CAF50;
  padding: 2px 8px;
  border-radius: 12px;
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-family: 'Roboto', sans-serif;
  white-space: nowrap;
`;

// Иконки в SVG формате
const TimeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="gray">
    <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"/>
    <path d="M13 7h-2v6l5.2 3.2 1.1-1.8-4.3-2.6z"/>
  </svg>
);

const CaloriesIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="gray">
    <path d="M12 3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2s2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
    <path d="M12 15c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
  </svg>
);

const LevelIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17h18v2H3zm0-6h18v2H3zm0-6h18v2H3z"/>
  </svg>
);

const TimeIconSvg = ({ fill = 'gray' }) => (
  <svg width="12" height="15" viewBox="0 0 12 15" fill="none">
    <path
      d="M6 2.5C4.81331 2.5 3.65328 2.85189 2.66658 3.51118C1.67989 4.17047 0.910851 5.10754 0.456726 6.2039C0.00259972 7.30026 -0.11622 8.50666 0.115291 9.67054C0.346802 10.8344 0.918247 11.9035 1.75736 12.7426C2.59648 13.5818 3.66558 14.1532 4.82946 14.3847C5.99335 14.6162 7.19975 14.4974 8.2961 14.0433C9.39246 13.5892 10.3295 12.8201 10.9888 11.8334C11.6481 10.8467 12 9.68669 12 8.5C11.9982 6.90926 11.3655 5.38419 10.2406 4.25937C9.11581 3.13455 7.59074 2.50182 6 2.5ZM6 13.5C5.0111 13.5 4.0444 13.2068 3.22215 12.6573C2.39991 12.1079 1.75904 11.327 1.38061 10.4134C1.00217 9.49979 0.90315 8.49445 1.09608 7.52455C1.289 6.55464 1.76521 5.66373 2.46447 4.96447C3.16373 4.2652 4.05465 3.789 5.02455 3.59607C5.99446 3.40315 6.99979 3.50216 7.91342 3.8806C8.82705 4.25904 9.60794 4.8999 10.1574 5.72215C10.7068 6.5444 11 7.51109 11 8.5C10.9985 9.82563 10.4713 11.0965 9.53389 12.0339C8.59653 12.9713 7.32563 13.4985 6 13.5ZM8.85375 5.64625C8.90024 5.69269 8.93712 5.74783 8.96228 5.80853C8.98744 5.86923 9.0004 5.93429 9.0004 6C9.0004 6.06571 8.98744 6.13077 8.96228 6.19147C8.93712 6.25217 8.90024 6.30731 8.85375 6.35375L6.35375 8.85375C6.3073 8.90021 6.25215 8.93706 6.19145 8.9622C6.13075 8.98734 6.0657 9.00028 6 9.00028C5.93431 9.00028 5.86925 8.98734 5.80855 8.9622C5.74786 8.93706 5.69271 8.90021 5.64625 8.85375C5.5998 8.8073 5.56295 8.75215 5.53781 8.69145C5.51266 8.63075 5.49972 8.5657 5.49972 8.5C5.49972 8.4343 5.51266 8.36925 5.53781 8.30855C5.56295 8.24786 5.5998 8.19271 5.64625 8.14625L8.14625 5.64625C8.19269 5.59976 8.24783 5.56288 8.30853 5.53772C8.36923 5.51256 8.43429 5.49961 8.5 5.49961C8.56571 5.49961 8.63077 5.51256 8.69147 5.53772C8.75217 5.56288 8.80732 5.59976 8.85375 5.64625ZM4 1C4 0.867392 4.05268 0.740215 4.14645 0.646447C4.24022 0.552678 4.36739 0.5 4.5 0.5H7.5C7.63261 0.5 7.75979 0.552678 7.85356 0.646447C7.94732 0.740215 8 0.867392 8 1C8 1.13261 7.94732 1.25979 7.85356 1.35355C7.75979 1.44732 7.63261 1.5 7.5 1.5H4.5C4.36739 1.5 4.24022 1.44732 4.14645 1.35355C4.05268 1.25979 4 1.13261 4 1Z"
      fill={fill}
      fillOpacity="1"
    />
  </svg>
);

const CaloriesIconSvg = ({ fill = 'gray' }) => (
  <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
    <path
      d="M9.49312 8.58374C9.36347 9.30793 9.01506 9.97501 8.49478 10.4952C7.9745 11.0153 7.30734 11.3636 6.58313 11.4931C6.55563 11.4975 6.52784 11.4998 6.5 11.5C6.37458 11.5 6.25375 11.4528 6.16148 11.3678C6.06921 11.2829 6.01223 11.1664 6.00185 11.0414C5.99146 10.9164 6.02843 10.7921 6.10542 10.6931C6.18242 10.594 6.2938 10.5276 6.4175 10.5069C7.45312 10.3325 8.33187 9.45374 8.5075 8.41624C8.52971 8.28545 8.60297 8.16885 8.71115 8.09208C8.81934 8.0153 8.95359 7.98465 9.08438 8.00686C9.21516 8.02908 9.33176 8.10233 9.40854 8.21052C9.48531 8.3187 9.51534 8.45295 9.49312 8.58374ZM11.5 7.99999C11.5 9.45868 10.9205 10.8576 9.88909 11.8891C8.85764 12.9205 7.45869 13.5 6 13.5C4.54131 13.5 3.14236 12.9205 2.11091 11.8891C1.07946 10.8576 0.5 9.45868 0.5 7.99999C0.5 6.25499 1.1875 4.47062 2.54125 2.69687C2.5841 2.6407 2.63837 2.59425 2.70047 2.56059C2.76258 2.52692 2.83112 2.50679 2.90157 2.50154C2.97201 2.49628 3.04278 2.50602 3.10919 2.5301C3.17561 2.55418 3.23616 2.59206 3.28687 2.64124L4.79437 4.10436L6.16938 0.32874C6.19685 0.253436 6.24202 0.185841 6.30108 0.131652C6.36015 0.0774634 6.43138 0.0382699 6.50877 0.0173761C6.58615 -0.00351779 6.66743 -0.00549969 6.74574 0.0115975C6.82406 0.0286947 6.89711 0.0643699 6.95875 0.115615C8.32562 1.24999 11.5 4.28437 11.5 7.99999ZM10.5 7.99999C10.5 5.11937 8.26312 2.62999 6.86187 1.35437L5.47 5.17124C5.44143 5.24963 5.3937 5.31963 5.33116 5.37486C5.26861 5.43008 5.19324 5.46878 5.11191 5.48742C5.03059 5.50606 4.94589 5.50405 4.86553 5.48157C4.78518 5.4591 4.71173 5.41687 4.65188 5.35874L3.00375 3.75999C2.00562 5.20062 1.5 6.62499 1.5 7.99999C1.5 9.19346 1.97411 10.3381 2.81802 11.182C3.66193 12.0259 4.80653 12.5 6 12.5C7.19347 12.5 8.33807 12.0259 9.18198 11.182C10.0259 10.3381 10.5 9.19346 10.5 7.99999Z"
      fill={fill}
      fillOpacity="1"
    />
  </svg>
);

const BarChartIconSvg = ({ fill = 'currentColor' }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 13V6.75"
      stroke={fill}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.75 13V3"
      stroke={fill}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.25 13V10.5"
      stroke={fill}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
`;

// Создаем кэш вне компонента
const imageCache = new Map();

const getRandomNumber = () => {
  const random = Math.floor(Math.random() * 5) + 1;
  return random === 0 ? 1 : random;
};

const LimitMessage = styled.div`
  color: white;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  margin: 20px;
  line-height: 1.5;
`;

const AppStoreButton = styled(motion.a)`
  background: linear-gradient(to right, #6C2FF2, #8A2BE2);
  border: none;
  border-radius: 16px;
  padding: 12px 20px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 20px auto;
  width: 80%;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
`;

// Добавляем базовый шиммер
const ShimmerAnimation = styled(motion.div)`
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.05) 25%, 
    rgba(255, 255, 255, 0.1) 50%, 
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
  
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const TitleShimmer = styled(ShimmerAnimation)`
  height: 16px;
  width: 80%;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const InfoShimmer = styled(ShimmerAnimation)`
  height: 12px;
  width: 40px;
  border-radius: 4px;
  margin-right: 12px;
`;

const IngredientShimmer = styled(ShimmerAnimation)`
  height: 20px;
  width: 60px;
  border-radius: 12px;
  margin: 2px;
`;

const LabelShimmer = styled(ShimmerAnimation)`
  height: 12px;
  width: 120px;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const AnalizedCard = memo(({ recipe, imageUrl }) => {
  const { cardData, isLoading, isLimitReached } = useRecipeCard(recipe);
  const random = getRandomNumber();
  console.log('🚀 cardData', cardData);

  const truncateTitle = (title) => {
    if (!title) return 'Без названия';
    return title?.length > 25 ? `${title.substring(0, 25)}...` : title;
  };

  // Если лимит достигнут и нет сохраненных данных
  if (isLimitReached && !cardData) {
    return (
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="limit-message">
          Вы уже использовали свою демо-версию. Для полного доступа установите приложение.
        </div>
        <motion.a 
          className="app-store-button"
          href="https://apps.apple.com/your-app" 
          target="_blank"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Скачать в App Store
        </motion.a>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="image-container">
        {imageUrl ? (
          <motion.a 
            href="https://apps.apple.com/pl/app/chef-kats-ai/id6739225693"
            target="_blank"
            rel="noopener noreferrer"
          >
            <motion.img
              className="image"
              src={imageUrl}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              loading="lazy"
            />
          </motion.a>
        ) : (
            <motion.div
              className="spinner"
              animate={{ rotate: 360 }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "linear"
              }}
            />
        )}
      </div>

      <div className="content-container">
        {!isLoading && cardData ? (
          <>
            <h3 className="title">{truncateTitle(cardData.title)}</h3>

            <div className="info-row">
              <div className="info-item">
                <div className="info-icon">
                  <TimeIconSvg fill="gray" />
                </div>
                <span className="info-text">{cardData.cooktime}</span>
              </div>
              <div className="info-item">
                <div className="info-icon">
                  <CaloriesIconSvg fill="gray" />
                </div>
                <span className="info-text">{cardData.calories}</span>
              </div>
              <div className="info-item">
                <div className="info-icon">
                  <BarChartIconSvg fill={getLevelColor(cardData.level)} />
                </div>
                <span className="info-text" style={{color: getLevelColor(cardData.level)}}>
                  {cardData.level}
                </span>
              </div>
            </div>

            <div className="ingredients-section">
              <div className="ingredients-label">
                You have all ingredients:
              </div>
              <div className="ingredients-container">
                {cardData.usedIngredients?.slice(0, 3).map((ingredient, index) => (
                  <div className="ingredient" key={index}>
                    {ingredient}
                  </div>
                ))}
                <div className="ingredient">
                  + {random} 
                </div>
              </div>
            </div>
          </>
        ) : (
          // Шиммер для загрузки
          <>
            <div className="title-shimmer shimmer"></div>
            <div className="info-row">
              <div className="info-shimmer shimmer"></div>
              <div className="info-shimmer shimmer"></div>
              <div className="info-shimmer shimmer"></div>
            </div>
            <div className="ingredients-section">
              <div className="label-shimmer shimmer"></div>
              <div className="ingredients-container">
                <div className="ingredient-shimmer shimmer"></div>
                <div className="ingredient-shimmer shimmer"></div>
                <div className="ingredient-shimmer shimmer"></div>
                <div className="ingredient-shimmer shimmer"></div>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
});

export default AnalizedCard; 